import { useEffect, useState } from "react";
import { InventoryService } from "../product/Service";
import { CustomerService } from "../customer/Service";
import Select from "react-select";
import { Link, useParams } from "react-router-dom";
import { SaleService } from "./Service";
import { UploadService } from "../../utils/UploadService";
import { toast } from "react-hot-toast";
import {VariantService} from "../variant/Service";

const UpdateCreditNote = () => {
  const { id }: any = useParams();
  // Services
  const service = new SaleService();
  const inventoryService = new InventoryService();
  const customerService = new CustomerService();
  const uploadService = new UploadService();
  const variantService = new VariantService();

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [customer, setCustomer] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [discount, setDiscount] = useState(0);
  const [total, setTotal] = useState(0);
  const [products, setProducts] = useState<any[]>([]);
  const [taxableAmount, setTaxableAmount] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [roundOff, setRoundOff] = useState(false);
  const [variantOptions, setVariantOptions] = useState<{
    label: string,
    value: string,
    product: any,
    variant: any
  }[]>([]);

  // Options
  const [customerOptions, setCustomerOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [saleModeOptions, setSaleModeOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [productOptions, setProductOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [taxTypeOptions, setTaxTypeOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const loadVariants = async () => {
    const variants = await variantService.all();
    setVariantOptions(variants.map((v: any) => ({
      label: `${v.size} (${v.unit}) / ${v.type}`,
      value: v.id,
      product: v.product,
      variant: v
    })));
  }

  const loadDetails = async () => {
    const creditNote = await service.getCreditNote(parseInt(id!));
    if (creditNote) {
        setInvoiceNumber(creditNote.invoiceNumber);
        setCustomer({label: creditNote.customer.name, value: creditNote.customer.id});
        setDate(creditNote.date.split('T')[0].toString());
        setNote(creditNote.note);
        setDiscount(creditNote.discount);
        setProducts(creditNote.products.map((p: any) => ({
            ...p.product,
            quantity: p.quantity,
            salePrice: p.salePrice,
            purchasePrice: p.purchasePrice,
            discount: p.discount,
            variant: p.variant.id
        })));
    }
}

useEffect(() => {
    loadDetails();
}, []);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    // Product should not be empty
    if (products.length === 0) {
      toast.error("Please add products");
      return;
    }
    toast.loading("Updating credit note...");
    const data = {
      invoiceNumber: invoiceNumber,
      customer: customer?.value,
      date,
      note,
      discount,
      products: products.map((p: any) => ({
        product: p.id,
        quantity: p.quantity,
        salePrice: p.salePrice,
        purchasePrice: p.purchasePrice,
        discount: p.discount,
        variant: p.variant,
      })),
      roundOff,
    };
    const res = await service.updateCreditNote(parseInt(id!), data);
    toast.dismiss();
    if (res) {
      toast.success("Credit Note update successfully");
    }
  };

  // Set options
  useEffect(() => {
    const fetchCustomers = async () => {
      const customers = await customerService.all();
      setCustomerOptions(
        customers
          .filter((c: any) => c.type === "Customer")
          .map((v: any) => ({
            label: v.name,
            value: v.id,
          }))
      );
    };
    const fetchProducts = async () => {
      const products = await inventoryService.allProducts();
      setProductOptions(
        products.map((v: any) => ({ label: v.name, value: v.id }))
      );
      setAllProducts(products.map((v: any) => ({ ...v, quantity: 1 })));
    };
    const purchaseModeOptions = [
      { label: "Cash", value: "Cash" },
      { label: "Credit", value: "Credit" },
    ];
    setSaleModeOptions(purchaseModeOptions);
    fetchCustomers();
    fetchProducts();
    loadVariants();
  }, []);

  useEffect(() => {
    const taxableAmount = products.reduce((acc: number, product: any) => {
      return acc + product.quantity * product.purchasePrice - (product.quantity * product.purchasePrice * product.discount / 100);
    }, 0);
    setTaxableAmount(taxableAmount);

    const total = taxableAmount - 0;
    setTotal(total);
  }, [products, roundOff, discount]);

  return (
    <>
      <div className="card mb-0">
        <div className="card-body">
          <div className="content-page-header">
            <h5>Add Credit Note</h5>
          </div>
          <form onSubmit={handleSubmit} className="row">
            <div className="col-md-12">
              <div className="form-group-item border-0 mb-0">
                <div className="row align-item-center">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Credit Note ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Credit Note ID"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Invoice Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Purchases Number"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Select Customer <span className="text-danger">*</span>
                      </label>
                      <ul className="form-group-plus css-equal-heights">
                        <li>
                          <Select
                            name="customer"
                            options={customerOptions}
                            value={customer}
                            onChange={setCustomer}
                            required={true}
                          />
                        </li>
                        <li>
                          <Link
                            className="btn btn-primary form-plus-btn"
                            to={"/customers/add"}
                          >
                            <i className="fas fa-plus-circle" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <div className="input-block mb-3">
                      <label>
                        Credit Note Date <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="datetimepicker form-control"
                        placeholder="Select Date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required={true}
                      />
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="input-block mb-3">
                      <label>
                        Products <span className="text-danger">*</span>
                      </label>
                      <ul className="form-group-plus css-equal-heights">
                        <li>
                          <Select
                            name="product"
                            options={productOptions}
                            onChange={(e) => {
                              const product = allProducts.find(
                                (p: any) => p.id === e?.value
                              );
                              if (product) {
                                setProducts([...products, product]);
                              }
                            }}
                          />
                        </li>
                        <li>
                          <Link
                            className="btn btn-primary form-plus-btn"
                            to={"/products/add"}
                          >
                            <i className="fas fa-plus-circle" />
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group-item">
                <div className="card-table">
                  <div className="card-body">
                    <div className="table-responsive no-pagination">
                      <table className="table table-center table-hover datatable">
                        <thead className="thead-light">
                        <tr>
                          <th>Product / Service</th>
                          <th>Variant</th>
                          <th>Quantity</th>
                          <th>Rate</th>
                          <th>Discount</th>
                          <th>Amount</th>
                          <th className="no-sort">Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {products.map((product: any) => (
                            <tr key={product.id}>
                              <td>{product.name}</td>
                              <td>
                                <div className=" mb-3">
                                  <Select
                                      name="variant"
                                      menuPortalTarget={document.body}
                                      styles={{
                                        menuPortal: base => ({
                                          ...base,
                                          zIndex: 9999
                                        })
                                      }}
                                      value={variantOptions.find((v: any) => v.product['id'] === product.id && v.variant['id'] === product.variant)}
                                      options={variantOptions.filter((v: any) => v.product['id'] === product.id)}
                                      onChange={(e) => {
                                        const newProducts = products.map((p: any) => {
                                          if (p.id === product.id) {
                                            return {
                                              ...p,
                                              variant: parseInt(e?.value ?? '0'),
                                              purchasePrice: e?.variant?.salePrice ?? 0,
                                              salePrice: e?.variant?.salePrice ?? 0,
                                              discount: e?.variant?.discount ?? 0,
                                            };
                                          }
                                          return p;
                                        });
                                        setProducts(newProducts);
                                      }}
                                  />
                                </div>
                              </td>
                              <td>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter quantity"
                                    value={product.quantity}
                                    onChange={(e) => {
                                      const newProducts = products.map(
                                          (p: any) => {
                                            if (p.id === product.id) {
                                              return {
                                                ...p,
                                                quantity: parseInt(e.target.value),
                                              };
                                            }
                                            return p;
                                          }
                                      );
                                      setProducts(newProducts);
                                    }}
                                />
                              </td>
                              <td>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter purchase price"
                                    value={product.purchasePrice}
                                    onChange={(e) => {
                                      const newProducts = products.map((p: any) => {
                                        if (p.id === product.id) {
                                          return {
                                            ...p,
                                            purchasePrice: parseInt(e.target.value)
                                          };
                                        }
                                        return p;
                                      });
                                      setProducts(newProducts);
                                    }}
                                />
                              </td>
                              <td>{
                                product.discount
                              } %
                              </td>
                              <td>
                                ₹{product.purchasePrice * product.quantity - (product.purchasePrice * product.quantity * product.discount / 100)}
                              </td>
                              <td className="d-flex align-items-center h-100">
                                <button
                                    className="btn btn-danger btn-sm mb-2"
                                    onClick={() => {
                                      const newProducts = products.filter(
                                          (p: any) => p.id !== product.id
                                      );
                                      setProducts(newProducts);
                                    }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                        ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group-item border-0 p-0">
                <div className="row">
                  <div className="col-12">
                    <div className="form-group-bank">
                      <div className="invoice-total-box">
                        <div className="invoice-total-inner">
                          <p>
                            Taxable Amount <span>₹{taxableAmount}</span>
                          </p>
                          <p>
                            Discount <span>₹{totalDiscount}</span>
                          </p>
                          <div className="status-toggle justify-content-between">
                            <div className="d-flex align-center">
                              <p>Round Off </p>
                              <input
                                  id="rating_1"
                                  className="check"
                                  type="checkbox"
                                  defaultChecked={false}
                                  onChange={(e) => {
                                    setRoundOff(e.target.checked);
                                  }}
                              />
                              <label
                                  htmlFor="rating_1"
                                  className="checktoggle checkbox-bg"
                              >
                                checkbox
                              </label>
                            </div>
                            <span>₹{total}</span>
                          </div>
                        </div>
                        <div className="invoice-total-footer">
                          <h4>
                            Total Amount <span>₹{total}</span>
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="btn-path text-end">
                <button
                    type="reset"
                    onClick={() => {
                      window.history.back();
                    }}
                    className="btn btn-cancel bg-primary-light me-2"
                >
                  Back
                </button>
                <button type="submit" className="btn btn-primary">
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UpdateCreditNote;
